import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Lock, RefreshCw, AlertTriangle, Clock8, TrendingDown, Download,
  Mail, Phone, User, ExternalLink, Calendar, LayoutDashboard, ShieldCheck,
  Wallet, BookOpen, Ticket, ChevronRight, MessageCircle, CreditCard, CalendarClock, UserPlus, Video,
  CloudUpload, Utensils, Activity, Sparkles, TrendingUp, Megaphone, ClipboardList,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { adminRetentionOverview, adminContactsUnreadCount } from '../services/api';
import RetentionReminderModal from '../components/RetentionReminderModal';
import NewsletterTile from '../components/NewsletterTile';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('hu-HU'); } catch { return iso; }
};

const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminRetentionOverview(key.trim());
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
    } catch (_) { toast.error('Érvénytelen admin kulcs.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form onSubmit={submit} data-testid="admin-dashboard-login" className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl">
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <LayoutDashboard className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Admin Dashboard</h1>
        <Input data-testid="admin-dashboard-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
        <Button data-testid="admin-dashboard-login-btn" type="submit" disabled={busy || !key.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
          {busy ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

const StatChip = ({ label, tone = 'default' }) => {
  const map = {
    default:  'bg-gray-700/60 text-gray-200',
    warning:  'bg-amber-500/15 text-amber-300',
    danger:   'bg-red-500/15 text-red-300',
    success:  'bg-emerald-500/15 text-emerald-300',
    info:     'bg-blue-500/15 text-blue-300',
  };
  return (
    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${map[tone]}`}>
      {label}
    </span>
  );
};

const ClientCard = ({ row, kind, onRemind }) => {
  // Compute urgency badges
  const daysLeft = row.days_until_expiry;
  const inactive = row.inactive_days;
  const remaining = row.remaining_sessions;

  const mailto = `mailto:${row.email}?subject=${encodeURIComponent('Orosz Coaching – bérleted státusza')}`;
  const tel    = row.phone ? `tel:${row.phone}` : null;

  return (
    <div
      data-testid={`retention-card-${kind}-${row.user_id}`}
      className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 hover:border-yellow-600/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center shrink-0">
            <User size={16} className="text-gray-400" />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-white truncate">{row.name || row.email}</div>
            <div className="text-xs text-gray-500 truncate">{row.email}</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {kind === 'expiring' && daysLeft !== null && (
            <StatChip label={daysLeft === 0 ? 'Ma jár le' : `${daysLeft} nap`} tone={daysLeft <= 3 ? 'danger' : 'warning'} />
          )}
          {kind === 'expiring' && remaining <= 2 && (
            <StatChip label={`${remaining} alk. maradt`} tone="danger" />
          )}
          {kind === 'at_risk' && (
            <StatChip label={`${inactive} napja inaktív`} tone={inactive >= 20 ? 'danger' : 'warning'} />
          )}
        </div>
      </div>

      <div className="text-xs text-gray-400 space-y-1">
        <div className="flex items-center gap-1.5">
          <Ticket size={12} className="text-gray-500" />
          <span className="text-gray-300">{row.program_title || row.tier_label || 'Bérlet'}</span>
          <span className="text-gray-500">·</span>
          <span>{row.remaining_sessions}/{row.total_sessions} alkalom</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock8 size={12} className="text-gray-500" />
          <span>Lejár: {fmtHuDate(row.expires_at)}</span>
          {row.frozen_until && (
            <span className="ml-2 text-blue-300">· Fagyasztva {fmtHuDate(row.frozen_until)}-ig</span>
          )}
        </div>
        {kind === 'at_risk' && (
          <div className="flex items-center gap-1.5">
            <Calendar size={12} className="text-gray-500" />
            <span>Utolsó aktivitás: {row.last_activity_at ? fmtHuDate(row.last_activity_at) : '— (soha)'}</span>
          </div>
        )}
        {kind === 'expiring' && row.expiring_notified_at && (
          <div className="flex items-center gap-1.5 text-emerald-300">
            <Mail size={12} />
            <span>Kupon email kiküldve · {row.expiring_coupon_code || ''}</span>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <a
          data-testid={`retention-email-${row.user_id}`}
          href={mailto}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-200 transition-colors"
        >
          <Mail size={12} /> Email
        </a>
        {tel && (
          <a
            data-testid={`retention-phone-${row.user_id}`}
            href={tel}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-gray-800 hover:bg-gray-700 text-gray-200 transition-colors"
          >
            <Phone size={12} /> {row.phone}
          </a>
        )}
        {kind === 'at_risk' && onRemind && (
          <button
            type="button"
            data-testid={`retention-remind-${row.user_id}`}
            onClick={() => onRemind(row)}
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 transition-colors"
          >
            <MessageCircle size={12} /> Személyes emlékeztető
          </button>
        )}
        <Link
          to="/admin/foglalasok"
          data-testid={`retention-bookings-${row.user_id}`}
          className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-yellow-400 hover:text-yellow-300"
        >
          Foglalások <ExternalLink size={11} />
        </Link>
      </div>
    </div>
  );
};

const NavTile = ({ to, icon: Icon, title, desc, testId, badge }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group rounded-xl border border-gray-800 bg-gray-900/60 p-4 hover:border-yellow-600/50 hover:bg-gray-900/90 transition-colors"
  >
    <div className="flex items-start gap-3">
      <div className="relative w-10 h-10 rounded-lg bg-yellow-600/15 flex items-center justify-center shrink-0">
        <Icon size={18} className="text-yellow-500" />
        {badge > 0 && (
          <span
            data-testid={`${testId}-badge`}
            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center shadow-lg ring-2 ring-gray-900"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="font-bold text-white text-sm truncate">{title}</div>
            {badge > 0 && (
              <span className="inline-flex items-center justify-center text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500 text-white shrink-0">
                {badge} új
              </span>
            )}
          </div>
          <ChevronRight size={14} className="text-gray-600 group-hover:text-yellow-500 transition-colors shrink-0" />
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{desc}</div>
      </div>
    </div>
  </Link>
);

const InactivityCard = ({ adminKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/inactivity/preview`,
        { headers: { 'X-Admin-Key': adminKey } });
      setData(r.data);
    } catch { /* silent */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    if (!window.confirm(`Elküldöm a heti inaktivitási digest emailt? (${data?.count} kliens)`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/inactivity/run`,
        {}, { headers: { 'X-Admin-Key': adminKey } });
      if (r.data.email_sent) toast.success('Email elküldve.');
      else if (r.data.at_risk === 0) toast.success('Senki nem inaktív — email nem indokolt.');
      else toast.error('A küldés nem sikerült.');
    } catch (e) {
      toast.error('Hiba: ' + (e?.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  const clients = data?.clients || [];
  const count = data?.count || 0;
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="inactivity-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-red-600/15 flex items-center justify-center">
            <AlertTriangle size={16} className="text-red-400" />
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">Inaktív ügyfelek (14+ nap)</h3>
            <p className="text-xs text-gray-500">Automata riasztás minden hétfő 08:00 UTC</p>
          </div>
        </div>
        <span data-testid="inactivity-count" className={`text-2xl font-bold ${count > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{loading ? '…' : count}</span>
      </div>
      {!loading && clients.length > 0 && (
        <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto pr-1">
          {clients.slice(0, 6).map(c => (
            <Link key={c.user_id} to={`/admin/haladas?user=${c.user_id}`} className="flex items-center justify-between text-xs py-1 hover:text-yellow-400">
              <span className="text-gray-300 truncate">{c.name || c.email}</span>
              <span className={`ml-2 px-2 py-0.5 rounded font-bold ${c.days_since >= 21 ? 'bg-red-600/20 text-red-400' : 'bg-amber-600/20 text-amber-400'}`}>
                {c.days_since}n
              </span>
            </Link>
          ))}
          {clients.length > 6 && <div className="text-xs text-gray-500 text-center">+{clients.length - 6} további…</div>}
        </div>
      )}
      <Button
        onClick={sendNow}
        disabled={sending || count === 0}
        data-testid="btn-send-inactivity"
        size="sm"
        className="w-full bg-red-600/80 hover:bg-red-600 text-white disabled:opacity-40"
      >
        {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Mail size={14} className="mr-1.5" />}
        {count === 0 ? 'Nincs kinek küldeni' : 'Küldés most'}
      </Button>
    </div>
  );
};

const MonthlyReportCard = ({ adminKey }) => {
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const sendAll = async () => {
    if (!window.confirm('Ki fogja küldeni az előző havi riportot minden aktív ügyfélnek. Biztos?')) return;
    setSending(true);
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/client-reports/send-all`,
        {}, { headers: { 'X-Admin-Key': adminKey } });
      setLastResult(r.data);
      toast.success(`Riport küldve: ${r.data.sent} sikeres, ${r.data.skipped} átugorva, ${r.data.failed} hibás.`);
    } catch (e) {
      toast.error('Hiba: ' + (e?.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5" data-testid="monthly-report-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-yellow-600/15 flex items-center justify-center">
            <Sparkles size={16} className="text-yellow-500" />
          </div>
          <div>
            <h3 className="font-bold text-white text-sm">Havi haladási riportok</h3>
            <p className="text-xs text-gray-500">Automata küldés minden hónap 1-jén 09:00 UTC</p>
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-3 leading-relaxed">
        Személyre szabott „ajándék&rdquo; email minden aktív ügyfélnek az előző havi teljesítményéről: elvégzett edzések, testsúly-változás, kiemelt gyakorlat.
      </p>
      {lastResult && (
        <div className="text-xs text-gray-400 mb-3 border-t border-gray-800 pt-2">
          Utolsó kézi küldés: <span className="text-emerald-400 font-bold">{lastResult.sent} sikeres</span>
          {lastResult.skipped > 0 && <span className="text-gray-500"> · {lastResult.skipped} átugorva</span>}
          {lastResult.failed > 0 && <span className="text-red-400"> · {lastResult.failed} hibás</span>}
        </div>
      )}
      <Button
        onClick={sendAll}
        disabled={sending}
        data-testid="btn-send-monthly-reports"
        size="sm"
        className="w-full bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold"
      >
        {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Mail size={14} className="mr-1.5" />}
        Küldés minden aktív ügyfélnek most
      </Button>
    </div>
  );
};

const AdminDashboardPage = () => {
  useDocumentMeta({ title: 'Admin Dashboard | Orosz Coaching' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminderRow, setReminderRow] = useState(null);
  const [contactsUnread, setContactsUnread] = useState(0);

  const loadContactsUnread = useCallback(async () => {
    if (!adminKey) return;
    try {
      const r = await adminContactsUnreadCount(adminKey);
      setContactsUnread(r.unread || 0);
    } catch { /* silent */ }
  }, [adminKey]);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const res = await adminRetentionOverview(adminKey);
      setData(res);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Lejárt admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error(err?.response?.data?.detail || 'Betöltési hiba.');
      }
    } finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { if (adminKey) load(); }, [adminKey, load]);
  useEffect(() => {
    if (!adminKey) return;
    loadContactsUnread();
    const id = setInterval(loadContactsUnread, 60000);
    return () => clearInterval(id);
  }, [adminKey, loadContactsUnread]);

  const atRisk       = useMemo(() => data?.at_risk       || [], [data]);
  const expiringSoon = useMemo(() => data?.expiring_soon || [], [data]);
  const thresholds   = data?.thresholds || {};

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <LayoutDashboard className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Admin Dashboard</h1>
              <p className="text-xs text-gray-400">Retenciós tile-ok és gyors navigáció.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-dashboard-atlas-mirror"
              variant="ghost"
              size="sm"
              onClick={async () => {
                const toastId = toast.loading('Atlas szinkronizálás fut…');
                try {
                  const res = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/admin/atlas-mirror/run`,
                    {},
                    { headers: { 'X-Admin-Key': adminKey }, timeout: 120000 }
                  );
                  const d = res.data || {};
                  toast.success(
                    `Atlas szinkron kész: ${d.total_docs} dok / ${d.total_collections} kollekció (${d.duration_ms} ms)`,
                    { id: toastId }
                  );
                } catch (e) {
                  toast.error(
                    'Atlas szinkron nem sikerült: ' + (e?.response?.data?.detail || e.message),
                    { id: toastId }
                  );
                }
              }}
              className="text-gray-300 hover:text-white"
            >
              <CloudUpload size={14} className="mr-1.5" /> Atlas szinkron
            </Button>
            <Button
              data-testid="admin-dashboard-backup"
              variant="ghost"
              size="sm"
              onClick={async () => {
                try {
                  const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`, {
                    headers: { 'X-Admin-Key': adminKey },
                    responseType: 'blob',
                  });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `oroszcoaching-backup-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Adatmentés letöltve.');
                } catch (e) {
                  toast.error('Az adatmentés letöltése nem sikerült.');
                }
              }}
              className="text-gray-300 hover:text-white"
            >
              <Download size={14} className="mr-1.5" /> Adatmentés
            </Button>
            <Button data-testid="admin-dashboard-refresh" variant="ghost" size="sm" onClick={load} className="text-gray-300 hover:text-white">
              <RefreshCw size={14} className="mr-1.5" /> Frissítés
            </Button>
            <Button data-testid="admin-dashboard-logout" variant="ghost" size="sm" onClick={() => { localStorage.removeItem(ADMIN_KEY_STORAGE); setAdminKey(''); }} className="text-gray-400 hover:text-red-400">
              <LogOut size={14} />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 space-y-10">
        {/* Retention tiles */}
        <section>
          <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Retenciós figyelmeztetések</h2>
              <p className="text-xs text-gray-500 mt-1">
                Automatikus emailek + kupon már kimennek — ez a nézet a személyes utánkövetéshez.
              </p>
            </div>
            {data?.generated_at && (
              <span className="text-[11px] text-gray-500 font-mono">
                Frissítve: {new Date(data.generated_at).toLocaleTimeString('hu-HU')}
              </span>
            )}
          </div>

          {loading ? (
            <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-yellow-500 mb-2" size={28} /><p className="text-gray-400 text-sm">Betöltés…</p></div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* AT-RISK TILE */}
              <div data-testid="tile-at-risk" className="rounded-2xl border border-gray-800 bg-gradient-to-b from-red-950/30 to-gray-900/50 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-red-500/15 flex items-center justify-center">
                      <TrendingDown className="text-red-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">Veszélyeztetett ügyfelek</h3>
                      <p className="text-xs text-gray-500">
                        Aktív bérlet, de nincs foglalás az elmúlt {thresholds.at_risk_inactivity_days || 10} napban.
                      </p>
                    </div>
                  </div>
                  <div data-testid="tile-at-risk-count" className="text-3xl font-black text-red-400 leading-none">
                    {atRisk.length}
                  </div>
                </div>

                {atRisk.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    <ShieldCheck size={28} className="mx-auto mb-2 text-emerald-500/60" />
                    Minden ügyfél aktív. 
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {atRisk.map((row) => (
                      <ClientCard
                        key={row.pass_id}
                        row={row}
                        kind="at_risk"
                        onRemind={setReminderRow}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* EXPIRING TILE */}
              <div data-testid="tile-expiring" className="rounded-2xl border border-gray-800 bg-gradient-to-b from-amber-950/30 to-gray-900/50 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <AlertTriangle className="text-amber-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-white">Hamarosan lejáró</h3>
                      <p className="text-xs text-gray-500">
                        {thresholds.expiry_window_days || 7} napon belül lejár, vagy ≤ {thresholds.low_sessions_threshold || 2} alkalom van hátra.
                      </p>
                    </div>
                  </div>
                  <div data-testid="tile-expiring-count" className="text-3xl font-black text-amber-400 leading-none">
                    {expiringSoon.length}
                  </div>
                </div>

                {expiringSoon.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    <ShieldCheck size={28} className="mx-auto mb-2 text-emerald-500/60" />
                    Nincs sürgős lejárat.
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
                    {expiringSoon.map((row) => (
                      <ClientCard key={row.pass_id} row={row} kind="expiring" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Retention automation — inactivity + monthly gift reports */}
        <section>
          <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Retenciós automatizmusok</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <InactivityCard adminKey={adminKey} />
            <MonthlyReportCard adminKey={adminKey} />
          </div>
        </section>

        {/* Newsletter */}
        <section>
          <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Hírlevél</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <NewsletterTile adminKey={adminKey} />
          </div>
        </section>

        {/* Quick navigation */}
        <section>
          <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Gyors navigáció</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <NavTile testId="nav-tile-slots" to="/admin/slots" icon={Calendar} title="Időpontok" desc="Konzultációs slotok kezelése." />
            <NavTile testId="nav-tile-settings" to="/admin/beallitasok" icon={ClipboardList} title="Beállítások" desc="Fizetős / ingyenes állapotfelmérés kapcsoló." />
            <NavTile testId="nav-tile-bookings" to="/admin/foglalasok" icon={BookOpen} title="Foglalások" desc="Kliens edzés-foglalások (megjelent / no-show)." />
            <NavTile testId="nav-tile-consultations" to="/admin/konzultaciok" icon={Video} title="Online konzultációk" desc="Egyszeri konzultáció rendelések és teljesítésük." />
            <NavTile testId="nav-tile-transfers" to="/admin/utalasok" icon={Wallet} title="Utalások" desc="Banki utalás jóváhagyás / lemondás." />
            <NavTile testId="nav-tile-blog" to="/admin/blog" icon={BookOpen} title="Blog editor" desc="Cikkek szerkesztése és ütemezése." />
            <NavTile testId="nav-tile-compliance" to="/admin/compliance" icon={ShieldCheck} title="Compliance" desc="GDPR audit és feliratkozók." />
            <NavTile testId="nav-tile-smoke-test" to="/admin/smoke-test" icon={CreditCard} title="Barion smoke test" desc="100 Ft-os élő tranzakció teszt (belső)." />
            <NavTile testId="nav-tile-leads" to="/admin/leadek" icon={User} title="Leadek" desc="Kalkulátor-használók, eredmények, drip állapot." />
            <NavTile testId="nav-tile-training" to="/admin/edzestervek" icon={CalendarClock} title="Edzéstervek" desc="Tervek készítése, aktiválása, vendég haladás." />
            <NavTile testId="nav-tile-meal-plans" to="/admin/etrendtervek" icon={Utensils} title="Étrendtervek" desc="Étrend generálása 415 receptből, kalória-cél a kalkulátorból." />
            <NavTile testId="nav-tile-progress" to="/admin/haladas" icon={Activity} title="Ügyfél haladás" desc="Testsúly + körméret grafikon, mérési előzmények, edzésnaplók." />
            <NavTile testId="nav-tile-custom-offers" to="/admin/egyedi-ajanlatok" icon={CreditCard} title="Egyedi ajánlatok" desc="Személyre szabott fizetési linkek egyedi árakkal (családi csomag, stb.)." />
            <NavTile testId="nav-tile-blog-analytics" to="/admin/cikk-figyelo" icon={TrendingUp} title="Cikk figyelő" desc="Melyik cikket hányszor nyitották meg és mennyi ideig olvasták." />
            <NavTile testId="nav-tile-users" to="/admin/fiokok" icon={UserPlus} title="Fiókok" desc="Teszt fiókok létrehozása jelszóval, törlés." />
            <NavTile testId="nav-tile-feedback" to="/admin/visszajelzesek" icon={MessageCircle} title="Visszajelzések" desc="Tesztelői hibajelentések és ötletek a portálról." />
            <NavTile testId="nav-tile-contacts" to="/admin/kapcsolat" icon={Mail} title="Kapcsolat" desc="Chat widget + kapcsolat űrlap üzenetek." badge={contactsUnread} />
            <NavTile testId="nav-tile-promo" to="/admin/promo" icon={Megaphone} title="Promó felugró" desc="Főoldali akciós felugró ablak szerkesztése élő előnézettel." />
            <NavTile testId="nav-tile-coupons" to="/admin/kuponok" icon={Ticket} title="Kuponok" desc="Kuponkódok generálása (% vagy Ft), beváltás követése." />
          </div>
        </section>
      </main>

      <RetentionReminderModal
        open={!!reminderRow}
        onClose={() => setReminderRow(null)}
        adminKey={adminKey}
        row={reminderRow}
        onSent={() => { setReminderRow(null); load(); }}
      />
    </div>
  );
};

export default AdminDashboardPage;
